import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { API, trackWaClick } from "@/lib/api";

function WhatsAppIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
    </svg>
  );
}

export default function ChatWidget() {
  const { content } = useSite();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const welcome = content?.chat?.welcome || "¡Hola! Soy el asistente de AUREX. ¿En qué notebook estás pensando?";
  const chatTitle = content?.chat?.title || "Asistente AUREX";
  const statusLabel = content?.chat?.status_label || "En línea · IA";
  const inputPlaceholder = content?.chat?.input_placeholder || "Escribí tu consulta…";
  const errorMessage = content?.chat?.error_message || "Ups, no pude responder. Escribinos por WhatsApp y te ayudamos.";
  const [messages, setMessages] = useState([{ role: "assistant", content: welcome }]);
  const scrollRef = useRef(null);

  // sync welcome message if content loads after mount and no conversation started yet
  useEffect(() => {
    setMessages((m) => (m.length === 1 && m[0].role === "assistant" ? [{ role: "assistant", content: welcome }] : m));
  }, [welcome]);

  const waNumber = (content?.contact?.whatsapp || "3624723836").replace(/\D/g, "");
  const waLink = `https://wa.me/54${waNumber}?text=${encodeURIComponent(content?.contact?.whatsapp_message || "Hola AUREX")}`;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          let data;
          try {
            data = JSON.parse(line);
          } catch {
            continue;
          }
          if (data.session_id) setSessionId(data.session_id);
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + data.delta,
              };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: errorMessage,
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }, [input, busy, sessionId, errorMessage]);

  return (
    <>
      {/* Floating buttons */}
      <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3">
        <a
          data-testid="floating-whatsapp"
          href={waLink}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackWaClick("floating")}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/40 transition-transform duration-300 hover:scale-110"
          aria-label="WhatsApp"
        >
          <WhatsAppIcon />
        </a>
        <button
          data-testid="chat-toggle"
          onClick={() => setOpen((o) => !o)}
          className="flex h-14 w-14 items-center justify-center rounded-full copper-bg text-[color:var(--aurex-on-accent)] copper-glow transition-transform duration-300 hover:scale-110"
          aria-label="Asistente AUREX"
        >
          {open ? <X size={24} /> : <MessageSquare size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-28 right-6 z-[60] flex h-[540px] w-[calc(100vw-3rem)] max-w-[390px] flex-col overflow-hidden rounded-3xl border border-white/10 glass shadow-2xl shadow-black/60"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full copper-bg text-[color:var(--aurex-on-accent)]">
                <Sparkles size={17} />
              </span>
              <div>
                <div className="font-display text-sm font-bold text-[color:var(--aurex-text)]">{chatTitle}</div>
                <div className="flex items-center gap-1.5 font-mono-jb text-[10px] uppercase tracking-[0.14em] text-[color:var(--aurex-muted)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--aurex-accent)]" /> {statusLabel}
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "copper-bg text-[color:var(--aurex-on-accent)]"
                        : "border border-white/10 bg-white/[0.04] text-[color:var(--aurex-text)]"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 p-3">
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={inputPlaceholder}
                  className="flex-1 bg-transparent text-sm text-[color:var(--aurex-text)] outline-none placeholder:text-zinc-600"
                />
                <button
                  data-testid="chat-send"
                  onClick={send}
                  disabled={busy}
                  className="flex h-8 w-8 items-center justify-center rounded-full copper-bg text-[color:var(--aurex-on-accent)] transition-opacity disabled:opacity-40"
                >
                  <Send size={15} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
